import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const MonitoringEvaluationAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    meFramework: {
      definedTool: data.meFramework?.definedTool || 'no',
      onboardingToExit: data.meFramework?.onboardingToExit || 'no',
      entrepreneurIndicators: data.meFramework?.entrepreneurIndicators || 'no',
      businessIndicators: data.meFramework?.businessIndicators || 'no',
      notes: data.meFramework?.notes || '',
    },
    progressReviews: {
      monthlyEvaluations: data.progressReviews?.monthlyEvaluations || 'no',
      quarterlyEvaluations: data.progressReviews?.quarterlyEvaluations || 'no',
      endOfProgram: data.progressReviews?.endOfProgram || 'no',
      systematicFeedback: data.progressReviews?.systematicFeedback || 'no',
      notes: data.progressReviews?.notes || '',
    },
    impactMeasurement: {
      mindsetImprovement: data.impactMeasurement?.mindsetImprovement || 'no',
      businessModelQuality: data.impactMeasurement?.businessModelQuality || 'no',
      revenueTraction: data.impactMeasurement?.revenueTraction || 'no',
      kpiResults: data.impactMeasurement?.kpiResults || 'no',
      notes: data.impactMeasurement?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const meFramework = assessmentData.meFramework;
    totalPoints += getPoints(meFramework.definedTool);
    totalPoints += getPoints(meFramework.onboardingToExit);
    totalPoints += getPoints(meFramework.entrepreneurIndicators);
    totalPoints += getPoints(meFramework.businessIndicators);
    maxPoints += 4;

    const progressReviews = assessmentData.progressReviews;
    totalPoints += getPoints(progressReviews.monthlyEvaluations);
    totalPoints += getPoints(progressReviews.quarterlyEvaluations);
    totalPoints += getPoints(progressReviews.endOfProgram);
    totalPoints += getPoints(progressReviews.systematicFeedback);
    maxPoints += 4;

    const impactMeasurement = assessmentData.impactMeasurement;
    totalPoints += getPoints(impactMeasurement.mindsetImprovement);
    totalPoints += getPoints(impactMeasurement.businessModelQuality);
    totalPoints += getPoints(impactMeasurement.revenueTraction);
    totalPoints += getPoints(impactMeasurement.kpiResults);
    maxPoints += 4;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <ClipboardDocumentListIcon className="h-8 w-8 text-orange-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Monitoring & Evaluation (M&E)</h2>
            <p className="text-gray-600">
              Assess the effectiveness of monitoring, evaluation, and impact measurement systems
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-orange-600 px-6 py-3 font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">M&E Framework</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a defined M&E tool that tracks progress from onboarding to exit?
            </label>
            <p className="text-xs text-gray-600">
              Comprehensive system for monitoring progress throughout the entire program lifecycle
            </p>
            <YesPartialNoRadio
              name="definedTool"
              value={assessmentData.meFramework.definedTool}
              onChange={value => updateField('meFramework', 'definedTool', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the M&E system cover the complete entry-to-exit journey?
            </label>
            <p className="text-xs text-gray-600">
              Tracking from initial program entry through to successful exit or graduation
            </p>
            <YesPartialNoRadio
              name="onboardingToExit"
              value={assessmentData.meFramework.onboardingToExit}
              onChange={value => updateField('meFramework', 'onboardingToExit', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneur development indicators included?
            </label>
            <p className="text-xs text-gray-600">
              Metrics that measure personal development, skills acquisition, and mindset changes
            </p>
            <YesPartialNoRadio
              name="entrepreneurIndicators"
              value={assessmentData.meFramework.entrepreneurIndicators}
              onChange={value => updateField('meFramework', 'entrepreneurIndicators', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are business development indicators included?
            </label>
            <p className="text-xs text-gray-600">
              Metrics that measure business growth, revenue, customer acquisition, and operational
              improvements
            </p>
            <YesPartialNoRadio
              name="businessIndicators"
              value={assessmentData.meFramework.businessIndicators}
              onChange={value => updateField('meFramework', 'businessIndicators', value)}
            />
          </div>

          <div>
            <label
              htmlFor="meFrameworkNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="meFrameworkNotes"
              value={assessmentData.meFramework.notes}
              onChange={e => updateField('meFramework', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ClockIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-900">Progress Reviews</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there scheduled monthly evaluations?
            </label>
            <p className="text-xs text-gray-600">
              Regular monthly assessments of progress and performance
            </p>
            <YesPartialNoRadio
              name="monthlyEvaluations"
              value={assessmentData.progressReviews.monthlyEvaluations}
              onChange={value => updateField('progressReviews', 'monthlyEvaluations', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there scheduled quarterly evaluations?
            </label>
            <p className="text-xs text-gray-600">
              Comprehensive quarterly reviews of progress and strategic adjustments
            </p>
            <YesPartialNoRadio
              name="quarterlyEvaluations"
              value={assessmentData.progressReviews.quarterlyEvaluations}
              onChange={value => updateField('progressReviews', 'quarterlyEvaluations', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there end-of-program evaluations?
            </label>
            <p className="text-xs text-gray-600">
              Final comprehensive assessment upon program completion or exit
            </p>
            <YesPartialNoRadio
              name="endOfProgram"
              value={assessmentData.progressReviews.endOfProgram}
              onChange={value => updateField('progressReviews', 'endOfProgram', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is feedback collected systematically from clients and coaches?
            </label>
            <p className="text-xs text-gray-600">
              Structured feedback mechanisms from both program participants and staff
            </p>
            <YesPartialNoRadio
              name="systematicFeedback"
              value={assessmentData.progressReviews.systematicFeedback}
              onChange={value => updateField('progressReviews', 'systematicFeedback', value)}
            />
          </div>

          <div>
            <label
              htmlFor="progressReviewsNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="progressReviewsNotes"
              value={assessmentData.progressReviews.notes}
              onChange={e => updateField('progressReviews', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ArrowTrendingUpIcon className="h-6 w-6 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-900">Impact Measurement</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program report improvement in entrepreneurial mindset?
            </label>
            <p className="text-xs text-gray-600">
              Evidence of positive changes in entrepreneurial attitudes, beliefs, and behaviors
            </p>
            <YesPartialNoRadio
              name="mindsetImprovement"
              value={assessmentData.impactMeasurement.mindsetImprovement}
              onChange={value => updateField('impactMeasurement', 'mindsetImprovement', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program report improvement in business model quality?
            </label>
            <p className="text-xs text-gray-600">
              Evidence of enhanced business model clarity, viability, and sophistication
            </p>
            <YesPartialNoRadio
              name="businessModelQuality"
              value={assessmentData.impactMeasurement.businessModelQuality}
              onChange={value => updateField('impactMeasurement', 'businessModelQuality', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program report improvement in revenue traction?
            </label>
            <p className="text-xs text-gray-600">
              Evidence of increased revenue, customer acquisition, and market traction
            </p>
            <YesPartialNoRadio
              name="revenueTraction"
              value={assessmentData.impactMeasurement.revenueTraction}
              onChange={value => updateField('impactMeasurement', 'revenueTraction', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are KPIs linked to actual commercial or operational results?
            </label>
            <p className="text-xs text-gray-600">
              Key performance indicators that measure real business outcomes and commercial success
            </p>
            <YesPartialNoRadio
              name="kpiResults"
              value={assessmentData.impactMeasurement.kpiResults}
              onChange={value => updateField('impactMeasurement', 'kpiResults', value)}
            />
          </div>

          <div>
            <label
              htmlFor="impactMeasurementNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="impactMeasurementNotes"
              value={assessmentData.impactMeasurement.notes}
              onChange={e => updateField('impactMeasurement', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default MonitoringEvaluationAssessment;
